import { useTheme } from '@mui/material'
import { PieChartComponent } from '../../../common/components/PieChartComponent/PieChartComponent'
import { useStyles } from '../RevenueByProductChart/RevenueByProductChart.styles'
import React from 'react'
import { PieCustomTooltip } from '../RevenueByProductChart/PieCustomTooltip/PieCustomTooltip'
import { CustomLegends } from '../CustomLegends/CustomLegends'
import { numberFormat } from '@labrav/common'
import { useSelector } from '../../../store/hooks'
import { Overview } from '../../../modules/flyoutOverview/types'
import { OverviewEmptyState } from '../OverviewEmptyState/OverviewEmptyState'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { getColorForProduct } from '../RevenueByProductChart/RevenueByProductChart'

export const RevenueViaISVChart = () => {
  const { classes } = useStyles()
  const theme = useTheme()

  const { revenue } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )
  const isRevenueViaISVDataEmpty =
    !revenue?.isvRevenue || revenue.isvRevenue.length === 0

  const revenueViaISV = revenue?.isvRevenue ?? []
  const datapie = revenueViaISV.slice(0, 3)
  const otherProducts = revenueViaISV[3]?.otherISV ?? []
  const otherProductsTotalValue = revenueViaISV[3]?.value || 0
  const otherProductsTotalPercentage = revenueViaISV[3]?.percentage || 0

  const topProducts = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        id: item.isv,
        label: item.isv,
        value: item.value,
        color: getColorForProduct(index),
      })),
    ...(otherProductsTotalValue !== 0
      ? [
          {
            id: 'Other',
            label: 'Other Products',
            value: otherProductsTotalValue,
            color: getColorForProduct(3),
          },
        ]
      : []),
  ]

  const percentageData: Record<string, number> = {
    ...datapie.reduce(
      (acc, item) => ({
        ...acc,
        [item.isv]: item.percentage,
      }),
      {} as Record<string, number>
    ),
    Other: otherProductsTotalPercentage,
  }

  const transformedOtherProducts = otherProducts
    .filter(item => item.value !== 0)
    .map(item => ({
      id: item.isv,
      label: item.isv,
      value: item.value,
      percentage: otherProductsTotalPercentage,
      color: theme.palette.awsGraph,
    }))

  const customLegendsData = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        label: item.isv,
        legendColor: getColorForProduct(index),
        legendSize: 'medium' as const,
        value: `USD ${numberFormat(item.value)}`,
        isKeyValue: true,
      })),
    ...(otherProductsTotalValue !== 0
      ? [
          {
            label: 'Other Products',
            legendColor: theme.palette.microsoftGraph.main,
            legendSize: 'medium' as const,
            value: `USD ${numberFormat(otherProductsTotalValue)}`,
            isKeyValue: true,
          },
        ]
      : []),
  ]

  return (
    <>
      {isRevenueViaISVDataEmpty ? (
        <OverviewEmptyState
          imgUrl={OverviewEmptyStateImage}
          containerHeight={17.5}
          imageHeight={12}
          imageWidth={16.75}
          description="No revenue has been generated by ISV."
        />
      ) : (
        <div
          className={classes.responsivePieWrapper}
          data-testid={'revenue-by-isv-chart'}
        >
          <div className={classes.leftContainer}>
            <PieChartComponent
              data={topProducts}
              height="184px"
              width="176px"
              colors={[
                theme.palette.awsGraph.main,
                theme.palette.gcpGraph.main,
                theme.palette.otherGraph.main,
                theme.palette.microsoftGraph.main,
              ]}
              tooltipContent={point => (
                <PieCustomTooltip
                  preFix="USD"
                  percentage={percentageData[point?.datum?.data.id]}
                  otherProducts={transformedOtherProducts}
                  point={point}
                />
              )}
            />
          </div>
          <div className={classes.rightContainer}>
            <CustomLegends flexDirection="column" legends={customLegendsData} />
          </div>
        </div>
      )}
    </>
  )
}
